/**
 * De que unidade é a Agenda (spec 019, fase 2).
 *
 * Com 2+ unidades a Agenda — dia, semana, mês, lista, painel lateral (dock)
 * e o Agora/Próximos — mostra só os agendamentos da UNIDADE ATUAL do
 * computador (`currentUnitId` do ClinicContext, o mesmo seletor do Topbar).
 * Admin e dono podem trocar pra "Todas as unidades" (decisão 5); aí cada
 * cartão leva a etiqueta da unidade, porque é o único jeito de saber pra
 * onde o paciente vai.
 *
 * Com uma unidade só, nada muda: não filtra e não etiqueta.
 *
 * Agendamento sem `unitId` (antigo) é da Sede. Com `unitId` de uma unidade
 * que não está na lista (inativada, apagada) também cai na Sede — senão ele
 * sumiria de todas as unidades e só apareceria em "Todas".
 */
import { SEDE_UNIT_ID } from './salas';

/** Valor do seletor pra "Todas as unidades". */
export const ALL_UNITS = '__todas__';

/** Unidade que recebe o agendamento sem unidade (ou de unidade que não está
 *  mais na lista): a Sede; sem Sede na lista, a primeira. */
export function homeUnitId(unitIds: readonly string[]): string {
  return unitIds.includes(SEDE_UNIT_ID) ? SEDE_UNIT_ID : (unitIds[0] ?? SEDE_UNIT_ID);
}

/** Unidade de um agendamento, entre as unidades da lista. */
export function appointmentUnitId(a: { unitId?: string | null }, unitIds: readonly string[]): string {
  return a.unitId && unitIds.includes(a.unitId) ? a.unitId : homeUnitId(unitIds);
}

/**
 * Unidade que a Agenda mostra, ou `null` = todas (sem filtro).
 *
 * - uma unidade só (ou nenhuma carregada) → `null`: nada muda;
 * - "Todas" escolhido por quem pode → `null`;
 * - senão a unidade atual; se ela não está na lista (gravada num computador
 *   e depois inativada), a Sede.
 *
 * `unitIds` são as unidades ATIVAS, na ordem das telas (`orderUnits`).
 */
export function resolveAgendaUnit(opts: {
  unitIds: readonly string[];
  currentUnitId: string | null | undefined;
  allUnits: boolean;
  canSeeAll: boolean;
}): string | null {
  const { unitIds, currentUnitId, allUnits, canSeeAll } = opts;
  if (unitIds.length < 2) return null;
  if (allUnits && canSeeAll) return null;
  return currentUnitId && unitIds.includes(currentUnitId) ? currentUnitId : homeUnitId(unitIds);
}

/** Os agendamentos da unidade (`null` = todos, como vieram). */
export function filterByUnit<T extends { unitId?: string | null }>(
  apts: T[],
  unitId: string | null,
  unitIds: readonly string[],
): T[] {
  if (unitId === null) return apts;
  return apts.filter((a) => appointmentUnitId(a, unitIds) === unitId);
}

/**
 * Nome da unidade por id pra etiqueta do cartão — só quando a Agenda está
 * MISTURANDO unidades (2+ unidades e "Todas"). Mostrando uma unidade só, a
 * etiqueta repetiria o seletor em cada cartão (mesma regra do nome do
 * médico). `undefined` = sem etiqueta.
 */
export function unitTagNames(
  units: ReadonlyArray<{ id: string; name: string }>,
  unitId: string | null,
): Record<string, string> | undefined {
  if (units.length < 2 || unitId !== null) return undefined;
  return Object.fromEntries(units.map((u) => [u.id, u.name]));
}
